import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { Reveal } from "../motion/Reveal";
import { SectionKicker } from "./Manifesto";
import { api } from "../../lib/api";

export const BlogPreview = () => {
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get(`/blog?lang=${lang}`).then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, [lang]);

  if (!posts.length) return null;

  return (
    <section id="stories" className="relative z-10 py-24 md:py-32 border-t border-white/10 bg-[#161616]" data-testid="blog-preview">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <SectionKicker>{t.blog.kicker}</SectionKicker>
              <h2 className="font-display font-extrabold tracking-tighter text-4xl md:text-6xl leading-[0.95]">
                {t.blog.title}
              </h2>
            </div>
            <Link
              to="/blog"
              data-testid="blog-all-link"
              className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-lime2 transition-colors border border-white/15 hover:border-lime2 rounded-full px-5 py-2.5 self-start"
            >
              {t.blog.all} <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block">
                <motion.div whileHover="hover" className="rounded-2xl overflow-hidden border border-white/10 bg-charcoal h-full">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <motion.img
                      src={p.cover}
                      alt={p.title}
                      variants={{ hover: { scale: 1.07 } }}
                      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] bg-black/60 backdrop-blur-md border border-white/20 rounded-full px-3 py-1 text-lime2">
                      {p.category}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-xl font-bold leading-snug group-hover:text-lime2 transition-colors">
                      {p.title}
                    </h3>
                    <p className="text-white/55 text-sm mt-3 line-clamp-2">{p.excerpt}</p>
                    <div className="mt-5 flex items-center justify-between text-xs text-white/40">
                      <span>{p.author} · {p.read_time} {t.blog.readTime}</span>
                      <ArrowUpRight size={16} className="text-white/40 group-hover:text-lime2 transition-colors" />
                    </div>
                  </div>
                </motion.div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
